import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "layout/dictionarySearch/folder/byFolder/documents/Header";
import { hoverSame, unhoverSame } from "utils";
import { useSelector } from "react-redux";
import { ReactComponent as EyeIcon } from "icons/icon-hide.svg";
import infoIcon from "icons/info.svg";
import { checkedTypes } from "constants/types";
import PropTypes from "prop-types";
import Gradient from "components/Gradient";
import {
  isEmptyDocument,
  setScrollbarPlaceholderForCategories,
} from "utils/dictionarySearch";
import InfiniteGrid from "components/Grid";
import { getIsCycle } from "utils/discovery";
import { TooltipTrigger } from "@visiblethread/react-components/dist/components/Tooltip";

const infoIconDiv = (setInfoHover) => (
  <div
    className="gradient__info"
    onMouseEnter={() => setInfoHover(true)}
    onMouseLeave={() => setInfoHover(false)}
  >
    <TooltipTrigger content="This file contains no text">
      <img src={infoIcon} alt="info" />
    </TooltipTrigger>
  </div>
);

const Documents = ({ categoriesRef, documentsRef }) => {
  const headerRef = useRef();
  const outerListRef = useRef(undefined);
  const innerListRef = useRef(undefined);
  const isCycle = useMemo(getIsCycle, []);

  const [infoHover, setInfoHover] = useState(false);
  const documents = useSelector(
    (state) => state.dictionarySearch.folder.documents
  );
  const categories = useSelector(
    (state) => state.dictionarySearch.folder.dictionary.categories
  );
  const [rows, setRows] = useState([]);

  const occurrences = categories
    .map((c) => c.documents.map((d) => d.occurrences))
    .flat(1);

  // using Array.reduce method to avoid stack overflow exceptions when using Math library on large arrays
  const maxOccurrences = occurrences?.reduce(
    (max, current) => Math.max(max, current),
    0
  );

  const uncheckedDocsIds = documents
    ?.filter((d) => d.checkState === checkedTypes.EMPTY)
    .map((d) => d.id);

  const docRow = (docs, firstRow) => {
    const documentsRow = Array(documents.length).fill(<Gradient content={0} />);
    docs.forEach((d, index) => {
      // Document empty - show info icon
      if (isEmptyDocument(documents[index]) && firstRow) {
        documentsRow.splice(index, 1, infoIconDiv(setInfoHover));
      }
      // Document unchecked - show eye icon
      if (
        uncheckedDocsIds.includes(d.id) &&
        firstRow &&
        !isEmptyDocument(documents[index])
      ) {
        documentsRow.splice(
          index,
          1,
          <div className="gradient__eye">
            <EyeIcon />
          </div>
        );
      }
      // Document checked - show gradient
      if (d.occurrences > 0 && !uncheckedDocsIds.includes(d.id)) {
        documentsRow.splice(
          index,
          1,
          <Gradient
            content={d.occurrences}
            maxOccurrences={maxOccurrences}
          />
        );
      }
    });
    return (
      <div className="documents__body__row" data-testid="document-row">
        {documentsRow}
      </div>
    );
  };

  const renderRow = ({ rowIndex, style }) => (
    <div
      style={style}
      className={`item item-${rowIndex}`}
      onMouseOver={() => hoverSame(rowIndex)}
      onFocus={() => hoverSame(rowIndex)}
      onMouseOut={unhoverSame}
      onBlur={unhoverSame}
    >
      {rows[rowIndex]}
    </div>
  );

  // Map state to rows
  useEffect(() => {
    const result = [];
    categories.forEach((category, index) => {
      result.push(docRow(category.documents, index === 0));
      if (category.expanded) {
        category.items?.forEach((item) => {
          result.push(docRow(item.documents));
        });
      }
    });
    setRows(result);
  }, [categories, infoHover]);

  const handleScroll = (scrollLeft, scrollTop) => {
    setScrollbarPlaceholderForCategories(categoriesRef, documentsRef);

    if (categoriesRef.current && !isCycle(scrollTop)) {
      categoriesRef.current.scrollTo({ scrollTop });
    }

    const header = headerRef.current;
    if (header && outerListRef.current) {
      // If body has scrollbar add it to header too.
      if (scrollLeft && header.style.overflowY !== "scroll") {
        header.style.overflowY = "scroll";
      } else if (!scrollLeft && header.style.overflowY !== "hidden") {
        header.style.overflowY = "hidden";
      }

      header.scrollLeft = scrollLeft;
    }
  };

  return (
    <div className="documents" data-testid="documents-pane">
      <Header headerRef={headerRef} />
      <div className="documents__body" data-testid="documents-body">
        <InfiniteGrid
          listRef={documentsRef}
          rowSize={26}
          outerRef={outerListRef}
          innerRef={innerListRef}
          rowCount={rows.length}
          onScroll={(scrollLeft, scrollTop) =>
            handleScroll(scrollLeft, scrollTop)
          }
          renderRow={renderRow}
          columnCount={1}
        />
      </div>
    </div>
  );
};

export default Documents;

Documents.propTypes = {
  categoriesRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  documentsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};
